import { useUser } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { apiService } from '@/services/api'
import { useUserInit } from '@/contexts/UserInitContext'

interface UseIsAdminResult {
  isAdmin: boolean;
  isLoading: boolean;
}

/**
 * Hook to check if the current user has admin privileges
 * Fetches admin status from the backend database (is_admin flag)
 * Uses React Query for caching with 5-minute staleTime
 *
 * Returns both isAdmin status and loading state to prevent race conditions
 * when used for conditional redirects.
 */
export function useIsAdmin(): boolean;
export function useIsAdmin(options: { withLoading: true }): UseIsAdminResult;
export function useIsAdmin(options?: { withLoading?: boolean }): boolean | UseIsAdminResult {
  const { isLoaded, isSignedIn } = useUser()
  const { isInitialized } = useUserInit()

  // Query the backend API for user profile which includes isAdmin field
  // Wait for user to be initialized in Supabase before fetching
  const { data, isLoading: isQueryLoading } = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => apiService.getProfile(),
    enabled: isLoaded && isSignedIn && isInitialized, // Wait for Clerk auth AND Supabase init
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: 1 // Retry once on failure
  })

  const isAdmin = data?.data?.isAdmin || false;

  // Loading if: Clerk not loaded, user init not done, or query still fetching
  const isLoading = !isLoaded || (isSignedIn && !isInitialized) || (isSignedIn && isInitialized && isQueryLoading);

  // Return object with loading state if requested
  if (options?.withLoading) {
    return { isAdmin, isLoading };
  }

  // Default: return just the boolean (backwards compatible)
  return isAdmin;
}
